'use client';

import { gtm } from '@/lib/gtm';

// Event categories for organization
export const EventCategories = {
  AUTH: 'auth',
  ORGANIZATION: 'organization',
  PROJECT: 'project',
  PARTICIPANT: 'participant',
  MATCHING: 'matching',
  SUBSCRIPTION: 'subscription',
  USER: 'user',
  ENGAGEMENT: 'engagement',
  DEMO: 'demo',
} as const;

// Define all trackable events
export interface TrackEventParams {
  event: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Core tracking function
export function trackEvent(params: TrackEventParams) {
  if (typeof window === 'undefined') return;
  
  gtm.push({
    event: params.event,
    event_category: params.category,
    event_action: params.action,
    event_label: params.label,
    event_value: params.value,
    ...params,
  });
}

// ============================================
// AUTH EVENTS
// ============================================

export function trackSignUp(provider: string, isDemo: boolean = false) {
  trackEvent({
    event: 'sign_up',
    category: EventCategories.AUTH,
    action: 'sign_up',
    label: provider,
    method: provider,
    is_demo: isDemo,
  });
}

export function trackSignIn(provider: string) {
  trackEvent({
    event: 'login',
    category: EventCategories.AUTH,
    action: 'sign_in',
    label: provider,
    method: provider,
  });
}

export function trackSignOut() {
  trackEvent({
    event: 'logout',
    category: EventCategories.AUTH,
    action: 'sign_out',
  });
}

// ============================================
// ORGANIZATION EVENTS
// ============================================

export function trackOrganizationCreated(orgId: string, industry?: string) {
  trackEvent({
    event: 'organization_created',
    category: EventCategories.ORGANIZATION,
    action: 'create',
    label: industry,
    org_id: orgId,
    industry,
  });
}

export function trackOrganizationSwitched(orgId: string, orgName: string) {
  trackEvent({
    event: 'organization_switched',
    category: EventCategories.ORGANIZATION,
    action: 'switch',
    label: orgName,
    org_id: orgId,
  });
}

export function trackTeamMemberInvited(orgId: string, role: string) {
  trackEvent({
    event: 'team_member_invited',
    category: EventCategories.ORGANIZATION,
    action: 'invite_member',
    label: role,
    org_id: orgId,
  });
}

// ============================================
// PROJECT EVENTS
// ============================================

export function trackProjectCreated(projectId: string, matchingType?: string, profileType?: string) {
  trackEvent({
    event: 'project_created',
    category: EventCategories.PROJECT,
    action: 'create',
    label: profileType,
    project_id: projectId,
    matching_type: matchingType,
    profile_type: profileType,
  });
}

export function trackProjectUpdated(projectId: string, field: string) {
  trackEvent({
    event: 'project_updated',
    category: EventCategories.PROJECT,
    action: 'update',
    label: field,
    project_id: projectId,
    updated_field: field,
  });
}

export function trackProjectStatusChanged(projectId: string, oldStatus: string, newStatus: string) {
  trackEvent({
    event: 'project_status_changed',
    category: EventCategories.PROJECT,
    action: 'status_change',
    label: `${oldStatus}_to_${newStatus}`,
    project_id: projectId,
    old_status: oldStatus,
    new_status: newStatus,
  });
}

export function trackProjectDeleted(projectId: string) {
  trackEvent({
    event: 'project_deleted',
    category: EventCategories.PROJECT,
    action: 'delete',
    project_id: projectId,
  });
}

export function trackProjectViewed(projectId: string, projectName?: string) {
  trackEvent({
    event: 'project_viewed',
    category: EventCategories.PROJECT,
    action: 'view',
    label: projectName,
    project_id: projectId,
  });
}

// ============================================
// PARTICIPANT EVENTS
// ============================================

export function trackParticipantAdded(projectId: string, count: number = 1) {
  trackEvent({
    event: 'participant_added',
    category: EventCategories.PARTICIPANT,
    action: 'add',
    value: count,
    project_id: projectId,
    participant_count: count,
  });
}

export function trackParticipantsImported(projectId: string, count: number, source: 'csv' | 'paste' | 'manual') {
  trackEvent({
    event: 'participants_imported',
    category: EventCategories.PARTICIPANT,
    action: 'import',
    label: source,
    value: count,
    project_id: projectId,
    import_source: source,
    participant_count: count,
  });
}

export function trackParticipantInviteSent(projectId: string, count: number = 1) {
  trackEvent({
    event: 'participant_invite_sent',
    category: EventCategories.PARTICIPANT,
    action: 'invite_sent',
    value: count,
    project_id: projectId,
    invite_count: count,
  });
}

export function trackParticipantCompleted(projectId: string) {
  trackEvent({
    event: 'participant_completed',
    category: EventCategories.PARTICIPANT,
    action: 'completed_questionnaire',
    project_id: projectId,
  });
}

export function trackParticipantRemoved(projectId: string, count: number = 1) {
  trackEvent({
    event: 'participant_removed',
    category: EventCategories.PARTICIPANT,
    action: 'remove',
    value: count,
    project_id: projectId,
  });
}

// ============================================
// MATCHING/SCENARIO EVENTS
// ============================================

export function trackMatchingStarted(projectId: string, matchingType: 'smart' | 'manual') {
  trackEvent({
    event: 'matching_started',
    category: EventCategories.MATCHING,
    action: 'start',
    label: matchingType,
    project_id: projectId,
    matching_type: matchingType,
  });
}

export function trackScenarioCreated(projectId: string, scenarioId: string, groupSize?: string) {
  trackEvent({
    event: 'scenario_created',
    category: EventCategories.MATCHING,
    action: 'create_scenario',
    project_id: projectId,
    scenario_id: scenarioId,
    group_size: groupSize,
  });
}

export function trackScenarioViewed(projectId: string, scenarioId: string) {
  trackEvent({
    event: 'scenario_viewed',
    category: EventCategories.MATCHING,
    action: 'view_scenario',
    project_id: projectId,
    scenario_id: scenarioId,
  });
}

export function trackResultsExported(projectId: string, format: 'csv' | 'json' | 'pdf') {
  trackEvent({
    event: 'results_exported',
    category: EventCategories.MATCHING,
    action: 'export',
    label: format,
    project_id: projectId,
    export_format: format,
  });
}

// ============================================
// SUBSCRIPTION EVENTS
// ============================================

export function trackSubscriptionStarted(planName: string, orgId: string) {
  trackEvent({
    event: 'subscription_started',
    category: EventCategories.SUBSCRIPTION,
    action: 'start',
    label: planName,
    plan_name: planName,
    org_id: orgId,
  });
}

export function trackSubscriptionUpgraded(oldPlan: string, newPlan: string, orgId: string) {
  trackEvent({
    event: 'subscription_upgraded',
    category: EventCategories.SUBSCRIPTION,
    action: 'upgrade',
    label: `${oldPlan}_to_${newPlan}`,
    old_plan: oldPlan,
    new_plan: newPlan,
    org_id: orgId,
  });
}

export function trackSubscriptionCanceled(planName: string, orgId: string) {
  trackEvent({
    event: 'subscription_canceled',
    category: EventCategories.SUBSCRIPTION,
    action: 'cancel',
    label: planName,
    plan_name: planName,
    org_id: orgId,
  });
}

export function trackPricingViewed(source?: string) {
  trackEvent({
    event: 'pricing_viewed',
    category: EventCategories.SUBSCRIPTION,
    action: 'view_pricing',
    label: source,
  });
}

export function trackCheckoutStarted(planName: string, orgId: string) {
  trackEvent({
    event: 'begin_checkout',
    category: EventCategories.SUBSCRIPTION,
    action: 'checkout_started',
    label: planName,
    plan_name: planName,
    org_id: orgId,
  });
}

// ============================================
// USER EVENTS
// ============================================

export function trackProfileUpdated(field: string) {
  trackEvent({
    event: 'profile_updated',
    category: EventCategories.USER,
    action: 'update_profile',
    label: field,
    updated_field: field,
  });
}

export function trackAccountDeleted(reason?: string) {
  trackEvent({
    event: 'account_deleted',
    category: EventCategories.USER,
    action: 'delete_account',
    label: reason,
  });
}

export function trackFeedbackSubmitted(category: string) {
  trackEvent({
    event: 'feedback_submitted',
    category: EventCategories.USER,
    action: 'submit_feedback',
    label: category,
  });
}

// ============================================
// ENGAGEMENT EVENTS
// ============================================

export function trackContactFormSubmitted(subject?: string) {
  trackEvent({
    event: 'contact_form_submitted',
    category: EventCategories.ENGAGEMENT,
    action: 'contact_submit',
    label: subject,
  });
}

export function trackNewsletterSubscribed(source?: string) {
  trackEvent({
    event: 'newsletter_subscribed',
    category: EventCategories.ENGAGEMENT,
    action: 'newsletter_signup',
    label: source,
  });
}

export function trackBlogPostViewed(postSlug: string, postTitle?: string) {
  trackEvent({
    event: 'blog_post_viewed',
    category: EventCategories.ENGAGEMENT,
    action: 'view_blog',
    label: postTitle || postSlug,
    post_slug: postSlug,
  });
}

export function trackDocumentationViewed(docPath: string) {
  trackEvent({
    event: 'documentation_viewed',
    category: EventCategories.ENGAGEMENT,
    action: 'view_docs',
    label: docPath,
  });
}

export function trackCTAClicked(ctaName: string, location: string) {
  trackEvent({
    event: 'cta_clicked',
    category: EventCategories.ENGAGEMENT,
    action: 'click_cta',
    label: ctaName,
    cta_name: ctaName,
    cta_location: location,
  });
}

// ============================================
// DEMO MODE EVENTS
// ============================================

export function trackDemoOrgCreated(userId: string) {
  trackEvent({
    event: 'demo_org_created',
    category: EventCategories.DEMO,
    action: 'create_demo_org',
    user_id: userId,
    is_demo: true,
  });
}

export function trackDemoProjectViewed(projectId: string) {
  trackEvent({
    event: 'demo_project_viewed',
    category: EventCategories.DEMO,
    action: 'view_demo_project',
    project_id: projectId,
    is_demo: true,
  });
}

export function trackDemoResultsExplored(projectId: string) {
  trackEvent({
    event: 'demo_results_explored',
    category: EventCategories.DEMO,
    action: 'explore_demo_results',
    project_id: projectId,
    is_demo: true,
  });
}

// ============================================
// FEATURE USAGE EVENTS
// ============================================

export function trackFeatureUsed(featureName: string, context?: string) {
  trackEvent({
    event: 'feature_used',
    category: EventCategories.ENGAGEMENT,
    action: 'use_feature',
    label: featureName,
    feature_name: featureName,
    context,
  });
}

export function trackQuestionBankAccessed(profileType?: string) {
  trackEvent({
    event: 'question_bank_accessed',
    category: EventCategories.ENGAGEMENT,
    action: 'access_question_bank',
    label: profileType,
  });
}

export function trackCustomQuestionCreated(profileType: string) {
  trackEvent({
    event: 'custom_question_created',
    category: EventCategories.ENGAGEMENT,
    action: 'create_custom_question',
    label: profileType,
  });
}

// ============================================
// ERROR TRACKING
// ============================================

export function trackError(errorType: string, errorMessage: string, context?: string) {
  trackEvent({
    event: 'error_occurred',
    category: 'error',
    action: errorType,
    label: errorMessage,
    error_type: errorType,
    error_message: errorMessage,
    error_context: context,
  });
}

// ============================================
// USER IDENTIFICATION (for logged-in users)
// ============================================

export function identifyUser(userId: string, traits?: {
  email?: string;
  name?: string;
  orgId?: string;
  plan?: string;
}) {
  if (typeof window === 'undefined') return;
  
  gtm.push({
    event: 'user_identified',
    user_id: userId,
    ...traits,
  });
}

export function resetUserIdentity() {
  if (typeof window === 'undefined') return;
  
  gtm.push({
    event: 'user_reset',
    user_id: null,
  });
}

